import React, { useState } from 'react';
import {
  Boxes,
  ArrowLeft,
  Search,
  Filter,
  Plus,
  Edit,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Package,
  Warehouse,
  BarChart3,
  Calendar,
  DollarSign,
  RefreshCw,
  Download,
  Upload,
  MapPin,
  User,
  Clock,
  Activity
} from 'lucide-react';
import { inventoryData } from '../../data/purchaseCycleData';

const InventoryManagement = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const { currentInventory, inventoryDashboard } = inventoryData;

  // Filter inventory items
  const filteredInventory = currentInventory.filter(item => {
    const matchesSearch = item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.itemCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.supplierName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'adequate': return 'text-green-600 bg-green-100';
      case 'reorder_needed': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      case 'overstock': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'adequate': return '庫存充足';
      case 'reorder_needed': return '需要補貨';
      case 'critical': return '庫存不足';
      case 'overstock': return '庫存過多';
      default: return '未知狀態';
    }
  };

  const getCategoryText = (category) => {
    switch (category) {
      case 'raw_materials': return '原物料';
      case 'components': return '零組件';
      case 'fasteners': return '緊固件';
      case 'seals': return '密封件';
      default: return category;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const renderWorkflowBanner = () => {
    if (!navigationSource) return null;
    
    return (
      <div className="mb-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Package className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-semibold">採購週期管理 - 庫存管理</h2>
              <p className="text-orange-100 text-sm">Purchase Cycle Management - Inventory Management</p>
            </div>
          </div>
          <button
            onClick={onBack}
            className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回採購週期</span>
          </button>
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">總庫存價值</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(inventoryDashboard.totalValue)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">月消耗 {formatCurrency(inventoryDashboard.monthlyConsumption)}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">庫存項目</p>
              <p className="text-2xl font-bold text-gray-900">{inventoryDashboard.totalItems}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Boxes className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Package className="w-4 h-4 text-blue-500 mr-1" />
            <span className="text-blue-600">庫存準確率 {inventoryDashboard.stockAccuracy}%</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">低庫存項目</p>
              <p className="text-2xl font-bold text-red-600">{inventoryDashboard.lowStockItems}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <XCircle className="w-4 h-4 text-red-500 mr-1" />
            <span className="text-red-600">需要補貨 {inventoryDashboard.reorderItems} 項</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">庫存周轉率</p>
              <p className="text-2xl font-bold text-gray-900">{inventoryDashboard.turnoverRate}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Clock className="w-4 h-4 text-purple-500 mr-1" />
            <span className="text-purple-600">平均前置時間 {inventoryDashboard.averageLeadTime} 天</span>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">庫存分類統計</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {inventoryDashboard.categories.map((category, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{getCategoryText(category.name)}</h4>
                <span className="text-sm text-gray-500">{category.count} 項</span>
              </div>
              <p className="text-lg font-semibold text-blue-600">{formatCurrency(category.value)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-5 h-5 text-blue-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">查看庫存清單</p>
              <p className="text-sm text-gray-500">檢視所有庫存項目</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('alerts')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">庫存警示</p>
              <p className="text-sm text-gray-500">查看需要補貨的項目</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('reports')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BarChart3 className="w-5 h-5 text-green-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">庫存報表</p>
              <p className="text-sm text-gray-500">生成庫存分析報表</p>
            </div>
          </button>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">最近活動</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">鋁合金管 6061 庫存不足</p>
              <p className="text-xs text-gray-500">2024-01-25 12:00</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
            <RefreshCw className="w-5 h-5 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">庫存盤點完成</p>
              <p className="text-xs text-gray-500">2024-01-25 10:30</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">鋼材板 A36 入庫完成</p>
              <p className="text-xs text-gray-500">2024-01-25 09:15</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInventoryList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">庫存清單</h2>
          <p className="text-gray-600">管理所有庫存項目</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
            <Plus className="w-4 h-4" />
            <span>新增項目</span>
          </button>
          <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            <Upload className="w-4 h-4" />
            <span>匯入</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋項目名稱或代碼..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="adequate">庫存充足</option>
            <option value="reorder_needed">需要補貨</option>
            <option value="critical">庫存不足</option>
            <option value="overstock">庫存過多</option>
          </select>
          
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有分類</option>
            <option value="raw_materials">原物料</option>
            <option value="components">零組件</option>
            <option value="fasteners">緊固件</option>
            <option value="seals">密封件</option>
          </select>
          
          <button className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors">
            <Download className="w-4 h-4" />
            <span>匯出</span>
          </button>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">項目</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">分類</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">庫存量</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">單位成本</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">總價值</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">供應商</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInventory.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img className="h-10 w-10 rounded-lg object-cover" src={item.image} alt={item.itemName} />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{item.itemName}</div>
                        <div className="text-sm text-gray-500">{item.itemCode}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getCategoryText(item.category)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.currentStock.toLocaleString()} {item.unit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(item.unitCost)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(item.totalValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                      {getStatusText(item.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.supplierName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedItem(item);
                          setActiveView('detail');
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-green-600 hover:text-green-900">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderItemDetail = () => {
    if (!selectedItem) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setActiveView('list')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedItem.itemName}</h2>
              <p className="text-gray-600">{selectedItem.itemCode}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <Edit className="w-4 h-4" />
              <span>編輯</span>
            </button>
            <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              <RefreshCw className="w-4 h-4" />
              <span>盤點</span>
            </button>
          </div>
        </div>

        {/* Item Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">基本資訊</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <img className="h-16 w-16 rounded-lg object-cover" src={selectedItem.image} alt={selectedItem.itemName} />
                <div>
                  <p className="font-medium text-gray-900">{selectedItem.itemName}</p>
                  <p className="text-sm text-gray-500">{selectedItem.itemNameEn}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">項目代碼</p>
                  <p className="font-medium text-gray-900">{selectedItem.itemCode}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">分類</p>
                  <p className="font-medium text-gray-900">{getCategoryText(selectedItem.category)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">單位</p>
                  <p className="font-medium text-gray-900">{selectedItem.unit}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">狀態</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedItem.status)}`}>
                    {getStatusText(selectedItem.status)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">庫存資訊</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">當前庫存</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedItem.currentStock.toLocaleString()} {selectedItem.unit}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">安全庫存</p>
                  <p className="text-lg font-medium text-orange-600">{selectedItem.safetyStock.toLocaleString()} {selectedItem.unit}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">再訂購點</p>
                  <p className="text-lg font-medium text-red-600">{selectedItem.reorderLevel.toLocaleString()} {selectedItem.unit}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">最大庫存</p>
                  <p className="text-lg font-medium text-blue-600">{selectedItem.maxStock.toLocaleString()} {selectedItem.unit}</p>
                </div>
              </div>
              
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">庫存水平</p>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${(selectedItem.currentStock / selectedItem.maxStock) * 100}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0</span>
                  <span>{selectedItem.maxStock.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">財務資訊</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">單位成本</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedItem.unitCost)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">總價值</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(selectedItem.totalValue)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">最後更新</p>
              <p className="text-lg font-medium text-gray-900">
                {new Date(selectedItem.lastUpdated).toLocaleDateString('zh-TW')}
              </p>
            </div>
          </div>
        </div>

        {/* Supplier Information */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">供應商資訊</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500">供應商名稱</p>
              <p className="font-medium text-gray-900">{selectedItem.supplierName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">前置時間</p>
              <p className="font-medium text-gray-900">{selectedItem.leadTime} 天</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">倉庫位置</p>
              <p className="font-medium text-gray-900">{selectedItem.location}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderInventoryList();
      case 'detail':
        return renderItemDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="p-6">
      {renderWorkflowBanner()}
      
      {/* Navigation Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveView('dashboard')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            儀表板
          </button>
          <button
            onClick={() => setActiveView('list')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            庫存清單
          </button>
        </nav>
      </div>

      {renderContent()}
    </div>
  );
};

export default InventoryManagement; 